import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  Toolbar,
  useTheme,
  useMediaQuery,
  Stack,
  Fade,
  Slide,
  Divider,
  Paper,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TrainingModal from "../components/TrainingModal";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
// Enhanced animated background component
const AnimatedBackground = () => (
  <Box
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: -1,
      background:
        "radial-gradient(circle at 50% 50%, #0A1929 0%, #000000 100%)",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        width: "200%",
        height: "200%",
        background:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2000 1500'%3E%3Cdefs%3E%3CradialGradient id='a' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23042644'/%3E%3Cstop offset='1' stop-color='%23000000'/%3E%3C/radialGradient%3E%3ClinearGradient id='b' gradientUnits='userSpaceOnUse' x1='0' y1='750' x2='1550' y2='750'%3E%3Cstop offset='0' stop-color='%23021322'/%3E%3Cstop offset='1' stop-color='%23000000'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23a)' width='2000' height='1500'/%3E%3Crect fill='url(%23b)' width='2000' height='1500'/%3E%3C/svg%3E\")",
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        opacity: 0.4,
        animation: "gradient 15s ease infinite",
        transform: "rotate(10deg)",
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "radial-gradient(circle at 25% 25%, rgba(25, 118, 210, 0.1) 0%, transparent 50%)",
        animation: "pulse 15s ease-in-out infinite alternate",
      },
      "@keyframes gradient": {
        "0%": {
          transform: "rotate(10deg) scale(1)",
        },
        "50%": {
          transform: "rotate(10deg) scale(1.2)",
        },
        "100%": {
          transform: "rotate(10deg) scale(1)",
        },
      },
      "@keyframes pulse": {
        "0%": {
          opacity: 0.3,
        },
        "50%": {
          opacity: 0.6,
        },
        "100%": {
          opacity: 0.3,
        },
      },
    }}
  />
);



// Enhanced Video Card Component
const VideoCard = ({ videoId }) => (
  <Box
    sx={{
      position: "relative",
      borderRadius: 4,
      overflow: "hidden",
      background: "rgba(255, 255, 255, 0.05)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
      "&:hover": {
        transform: "translateY(-10px)",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(100, 181, 246, 0.2)",
        "& .play-button": {
          backgroundColor: "#ff0000",
          transform: "translate(-50%, -50%) scale(1.1)",
        },
        "& .play-icon": {
          borderLeft: "24px solid #ffffff",
        },
        border: "1px solid rgba(100, 181, 246, 0.3)",
      },
    }}
  >
    <Box sx={{ paddingTop: "56.25%" }}>
      <iframe
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: "none",
        }}
        src={`https://www.youtube.com/embed/${videoId}`}
        title="Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </Box>
    <Box
      className="play-button"
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 68,
        height: 48,
        backgroundColor: "#909090",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.3s ease",
        opacity: 0.9,
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
      }}
    >
      <Box
        className="play-icon"
        sx={{
          width: 0,
          height: 0,
          borderTop: "12px solid transparent",
          borderBottom: "12px solid transparent",
          borderLeft: "24px solid #ffffff",
          transition: "all 0.3s ease",
          ml: "3px",
        }}
      />
    </Box>
  </Box>
);

// Enhanced Video Grid Component
const VideoGrid = ({ videos, columns }) => (
  <Grid container spacing={4} sx={{ mb: 8 }}>
    {videos.map((videoId, index) => (
      <Grid item xs={12} md={12 / columns} key={index}>
        <Fade in timeout={500} style={{ transitionDelay: `${index * 100}ms` }}>
          <Box>
            <VideoCard videoId={videoId} />
          </Box>
        </Fade>
      </Grid>
    ))}
  </Grid>
);

// Enhanced Hero Section with modern styling
const HeroSection = () => {
  const [animate, setAnimate] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setAnimate(true);
  }, []);

  return (
    <Box>
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 15, md: 20 },
          pb: 10,
          position: "relative",
        }}
      >
        <Slide in={animate} direction="down" timeout={1000}>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                background: "rgba(100, 181, 246, 0.1)",
                backdropFilter: "blur(5px)",
                borderRadius: 5,
                px: 3,
                py: 1,
                mb: 3,
                border: "1px solid rgba(100, 181, 246, 0.2)",
              }}
            >
              <AutoAwesomeIcon sx={{ color: "#64B5F6", mr: 1, fontSize: "1rem" }} />
              <Typography
                variant="overline"
                sx={{
                  color: "#64B5F6",
                  letterSpacing: 2,
                  fontWeight: 600,
                }}
              >
                Agents Already Above 10K Monthly Revenue   
              </Typography>
            </Box>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2rem", sm: "3rem", md: "4rem" },
                fontWeight: 900,
                background: "linear-gradient(45deg, #fff 30%, #64B5F6 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 3,
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
                textShadow: "0 5px 30px rgba(100, 181, 246, 0.3)",
              }}
            >
              How To Get Low-Cost, High-Ticket Life Insurance Leads
              with Referral-Level Trust
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "1.2rem", md: "1.5rem" },
                fontWeight: 400,
                color: "#B0BEC5",
                mb: 1,
                maxWidth: "800px",
                mx: "auto",
                lineHeight: 1.5,
              }}
            >
              & Learn How We Are Scaling Agencies to $1M/Month And Beyond
            </Typography>

         
          </Box>
        </Slide>

        {/* Enhanced Main Video Section */}
        

        {/* Booking Section - Commented out in original code */}
        {/* <Box sx={{ textAlign: "center", mb: 12 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#64B5F6",
              fontWeight: 700,
              mb: 4,
            }}
          >
            STEP 2: BOOK YOUR CALL
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 900,
              background: "linear-gradient(45deg, #64B5F6, #1976D2)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 4,
              fontSize: { xs: "2rem", md: "3rem" },
              letterSpacing: "0.02em",
            }}
          >
            BOOK NOW!
          </Typography>

          <Button
            onClick={() => navigate("/bookings")}
            variant="contained"
            size="large"
            sx={{
              position: "relative",
              overflow: "hidden",
              background: "linear-gradient(45deg, #1976D2, #64B5F6)",
              color: "white",
              fontWeight: 700,
              py: 3,
              px: 6,
              borderRadius: 3,
              fontSize: { xs: "1.1rem", md: "1.3rem" },
              boxShadow: "0 20px 40px rgba(25, 118, 210, 0.3)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent)",
                transition: "all 0.5s ease",
              },
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 25px 50px rgba(25, 118, 210, 0.4)",
                "&::before": {
                  left: "100%",
                },
              },
              transition: "all 0.3s ease",
            }}
          >
            <Stack spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                HIGH INTENT-AI LEADS
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                (BOOK YOUR CALL HERE)
              </Typography>
            </Stack>
          </Button>
        </Box> */}

        {/* Enhanced Proof Section */}
        <Box sx={{ textAlign: "center", position: "relative" }}>
          {/* Final CTA */}
          <Box sx={{ mt: 12, mb: { xs: 4, md: 16 } }}>
            <Fade in={animate} timeout={2000}>
              <Paper
                elevation={0}
                sx={{
                  background: "linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(100, 181, 246, 0.05))",
                  backdropFilter: "blur(10px)",
                  borderRadius: 5,
                  py: 6,
                  px: { xs: 3, md: 6 },
                  border: "1px solid rgba(100, 181, 246, 0.2)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "5px",
                    background: "linear-gradient(90deg, #1976D2, #64B5F6)",
                  },
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    background: "linear-gradient(45deg, #fff, #64B5F6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 6,
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    letterSpacing: "0.02em",
                    textShadow: "0 5px 30px rgba(100, 181, 246, 0.3)",
                  }}
                >
                  READY FOR AI GENERATED LEADS?
                </Typography>

                <Button
                  onClick={() => navigate("/lead-plans")}
                  variant="contained"
                  fullWidth
                  sx={{
                    background: "linear-gradient(45deg, #1976D2, #64B5F6)",
                    color: "white",
                    fontWeight: 900,
                    py: 2.5,
                    fontSize: { xs: "1.2rem", md: "1.5rem" },
                    maxWidth: "lg",
                    borderRadius: 3,
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 20px 40px rgba(25, 118, 210, 0.3)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent)",
                      transition: "all 0.8s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-5px) scale(1.02)",
                      boxShadow: "0 25px 50px rgba(25, 118, 210, 0.4)",
                      "&::before": {
                        left: "100%",
                      },
                    },
                    transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                    <span>See If You Qualify</span>
                    <ArrowForwardIcon />
                  </Stack>
                </Button>
              </Paper>
            </Fade>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// Enhanced Modern Footer
export const Footer = () => (
  <Box
    component="footer"
    sx={{
      py: 8,
      textAlign: "center",
      background: "rgba(10, 25, 41, 0.95)",
      backdropFilter: "blur(10px)",
      borderTop: "1px solid rgba(255, 255, 255, 0.1)",
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "1px",
        background: "linear-gradient(90deg, transparent, rgba(100, 181, 246, 0.3), transparent)",
      },
    }}
  >
    <Container maxWidth="lg">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 900,
          background: "linear-gradient(45deg, #fff, #64B5F6)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: 2,
          mb: 1.5,
          display: "inline-block",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -5,
            left: "25%",
            width: "50%",
            height: "2px",
            background: "linear-gradient(90deg, transparent, rgba(100, 181, 246, 0.5), transparent)",
          },
        }}
      >
        KALISUNMARKETING.COM
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: "#B0BEC5",
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          "& .star": {
            color: "#FFC107",
            fontSize: "1.5rem",
            animation: "pulse 2s infinite",
          },
          "@keyframes pulse": {
            "0%": { transform: "scale(1)" },
            "50%": { transform: "scale(1.2)" },
            "100%": { transform: "scale(1)" },
          },
        }}
      >
        <Link style={{color: "#0f8f0f"}} target="_blank" to="https://www.trustpilot.com/review/kalisunmarketing.com">Check Us Out On <span className="star">★</span> Trustpilot</Link>
        
      </Typography>

      <Divider sx={{ maxWidth: "200px", mx: "auto", my: 3, borderColor: "rgba(255,255,255,0.1)" }} />

      <Typography
        variant="caption"
        sx={{
          color: "#78909C",
          display: "block",
          maxWidth: "600px",
          mx: "auto",
          lineHeight: 1.8,
        }}
      >
        Copyright  kalisunmarketing.com 2024. All Rights Reserved.
        <br />
        Results shown are not typical and required hard work & dedication.
      </Typography>
    </Container>
  </Box>
);

// Main Component
const ValueVideo = () => {
  return (
     <Box
          sx={{
            bgcolor: "background.default",
            color: "text.primary",
            minHeight: "100vh",
          }}
        >
      <Navigation />
      <AnimatedBackground />
      
      <HeroSection />
      <Footer />
    </Box>
  );
};

export default ValueVideo;